import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Header, Icon, Input, Message, Modal, Table } from 'react-frontier';
import { useBreadcrumbs, useUser } from '../Hooks';
import { PromoterCondition, SetLoading } from '../SiteClasses';
import { bindClick, formatUnix } from '../SiteUtil';
import UserAccess from '../UserAccess';
import Validator from '../Validator';
import Toast from '../Toast';
import API from '../API';

enum Modals{
	CREATE = 1,
	DELETE = 2,
	INFO = 3,
	EDIT = 4,
}

interface ConditionForm{
	title: string,
	description: string,
}

var PromoterConditions = ()=>{
	var params = useParams<{ promoter_id?: string }>();
	var { setTitle } = useBreadcrumbs();
	var { hasAccess } = useUser();
	var promoter_id = params.promoter_id ? parseInt(params.promoter_id) : null;
	var canEdit = hasAccess(UserAccess.PROMOTERS.EDIT_PROMOTERS);

	var [conditions, setConditions] = useState<PromoterCondition[]>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [shownModal, setShownModal] = useState<number>(null);
	var [selected, setSelected] = useState<PromoterCondition>(null);
	var [form, setForm] = useState<ConditionForm>(null);
	var [editingId, setEditingId] = useState<number>(null);
	var [prompts, setPrompts] = useState<string[]>(null);

	useEffect(()=>{ setTitle('Condiciones'); }, []);
	useEffect(()=>{ get(); }, [promoter_id]);

	var get = ()=>{
		if(!promoter_id) return;
		setLoadError(null);
		API.getConditions(promoter_id).then(res=>{
			if(res.error) return setLoadError(res.message);
			setConditions(res.data || []);
		}).catch(()=>{
			setLoadError('Hubo un error cargando las condiciones. (LCL-1)');
		});
	};

	var openCreate = ()=>{
		setEditingId(null);
		setForm({ title: '', description: '' });
		setPrompts(null);
		setShownModal(Modals.CREATE);
	};

	var openInfo = (c: PromoterCondition)=>{
		setSelected(c);
		setShownModal(Modals.INFO);
	};

	var openEdit = (c: PromoterCondition)=>{
		setEditingId(c.note_id);
		setForm({ title: c.title, description: c.description || '' });
		setPrompts(null);
		setShownModal(Modals.EDIT);
	};

	var create = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(form, {
			title: [{ rule: 'length', params: [2, 128], label: 'Título' }],
		});
		setPrompts(prompts);
		if(!valid) return;

		setLoading(true);
		API.createCondition(promoter_id, {
			title: form.title.trim(),
			description: form.description.trim() || null,
		}).then(res=>{
			if(res.error) return setPrompts([res.message]);
			Toast.success('Condición agregada');
			setShownModal(null);
			get();
		}).catch(()=>{
			setPrompts(['Hubo un error agregando la condición. (LCL-1)']);
		}).finally(()=>setLoading(false));
	};

	var saveEdit = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(form, {
			title: [{ rule: 'length', params: [2, 128], label: 'Título' }],
		});
		setPrompts(prompts);
		if(!valid) return;

		setLoading(true);
		API.updateCondition(editingId, {
			title: form.title.trim(),
			description: form.description.trim() || null,
		}).then(res=>{
			if(res.error) return setPrompts([res.message]);
			Toast.success('Condición actualizada');
			setConditions(prev=> prev.map(x=> x.note_id===editingId ? {
				...x,
				title: form.title.trim(),
				description: form.description.trim() || null,
			} : x));
			setShownModal(null);
		}).catch(()=>{
			setPrompts(['Hubo un error actualizando la condición. (LCL-1)']);
		}).finally(()=>setLoading(false));
	};

	var onDelete = (c: PromoterCondition)=>{
		setSelected(c);
		setPrompts(null);
		setShownModal(Modals.DELETE);
	};

	var submitDelete = (setLoading: SetLoading)=>{
		setLoading(true);
		API.deleteCondition(selected.note_id).then(res=>{
			if(res.error) return setPrompts([res.message]);
			Toast.success('Condición eliminada');
			setConditions(prev=> prev.filter(x=>x.note_id!==selected.note_id));
			setShownModal(null);
		}).catch(()=>{
			setPrompts(['Hubo un error eliminando la condición. (LCL-1)']);
		}).finally(()=>setLoading(false));
	};

	var setField = (k: keyof ConditionForm)=> (v: any)=> setForm(f=>({ ...f, [k]: v }));

	if(loadError) return <Header text='Error' subtext={loadError} />;

	return <div style={{ paddingTop: 20 }}>
		<Table fitted striped style={{ maxWidth: 800, margin: '0 auto' }}>
			<Table.Head title='Condiciones' titleRight={canEdit ? (
				<Button text='Agregar' iconName='plus' size='small' color='black' onClick={openCreate} />
			) : undefined}>
				<Table.Row>
					<Table.Cell header collapsing>Título</Table.Cell>
					<Table.Cell header>Descripción</Table.Cell>
					<Table.Cell header collapsing centered><Icon name='wrench' /></Table.Cell>
				</Table.Row>
			</Table.Head>
			<Table.Body>
				{!conditions ? (
					<Table.Row><Table.Cell colSpan={3} centered><Header loading text='Cargando datos' /></Table.Cell></Table.Row>
				) : conditions.length===0 ? (
					<Table.Row><Table.Cell colSpan={3} centered>No hay condiciones registradas</Table.Cell></Table.Row>
				) : conditions.map(c=>(
					<Table.Row key={c.note_id}>
						<Table.Cell collapsing>{c.title}</Table.Cell>
						<Table.Cell>{c.description || <Icon name='minus' />}</Table.Cell>
						<Table.Cell collapsing centered className='no-select'>
							<Button basic icon iconName='ellipsis' size='small' menu={[
								{ text: 'Info', iconName: 'circle-info', onClick: ()=>openInfo(c) },
								{ text: 'Editar', iconName: 'pen', if: canEdit, onClick: ()=>openEdit(c) },
								{ text: 'Eliminar', iconName: 'trash', if: canEdit, onClick: ()=>onDelete(c) },
							]} />
						</Table.Cell>
					</Table.Row>
				))}
			</Table.Body>
		</Table>

		<Modal open={shownModal===Modals.CREATE || shownModal===Modals.EDIT} onClose={bindClick(setShownModal, null)} size='tiny'>
			<Modal.Header text={shownModal===Modals.EDIT ? 'Editar condición' : 'Agregar condición'} />
			<Modal.Content style={{ maxWidth: '100%', margin: 'auto' }}>
				{!!form && <>
					<Input fluid label='Título' placeholder='Título de la condición' value={form.title} onChange={setField('title')} />
					<Input fluid label='Descripción' placeholder='Descripción (opcional)' textarea textareaRows={3} value={form.description} onChange={setField('description')} />
					<Message type='error' list={prompts} />
				</>}
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic size='small' onClick={bindClick(setShownModal, null)} />
				<Button text='Guardar' color='black' size='small' onClick={shownModal===Modals.EDIT ? saveEdit : create} />
			</Modal.Actions>
		</Modal>

		<Modal open={shownModal===Modals.INFO} onClose={bindClick(setShownModal, null)}>
			<Modal.Header text='Detalle de condición' />
			<Modal.Content style={{ width: 500, maxWidth: '100%', margin: 'auto' }}>
				{!!selected && <Table fitted striped>
					<Table.Body>
						<Table.Row><Table.Cell collapsing><b>Título</b></Table.Cell><Table.Cell>{selected.title}</Table.Cell></Table.Row>
						<Table.Row><Table.Cell collapsing><b>Creador</b></Table.Cell><Table.Cell>{selected.user_creator_name || <Icon name='minus' />}</Table.Cell></Table.Row>
						<Table.Row><Table.Cell collapsing><b>Fecha</b></Table.Cell><Table.Cell>{formatUnix(selected.date_created, 'dd/MMM/yyyy HH:mm')}</Table.Cell></Table.Row>
						<Table.Row><Table.Cell collapsing><b>Descripción</b></Table.Cell><Table.Cell>{selected.description || <Icon name='minus' />}</Table.Cell></Table.Row>
					</Table.Body>
				</Table>}
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic size='small' onClick={bindClick(setShownModal, null)} />
			</Modal.Actions>
		</Modal>

		<Modal open={shownModal===Modals.DELETE} onClose={bindClick(setShownModal, null)}>
			<Modal.Header text='Eliminar condición' />
			<Modal.Content style={{ width: 500, maxWidth: '100%', margin: 'auto' }}>
				{!!selected && <Header size='small' text='¿Eliminar condición?' subtext={`Se eliminará "${selected.title}"`} />}
				<Message type='error' list={prompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic size='small' onClick={bindClick(setShownModal, null)} />
				<Button text='Eliminar' color='red' size='small' onClick={submitDelete} />
			</Modal.Actions>
		</Modal>
	</div>;
}

export default PromoterConditions;
